import React, { useCallback, useEffect, useState } from "react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { ErrorNote, Spinner } from "../components/ui.js";
import { useInvite } from "../team/useInvite.js";

/**
 * Who else works here.
 *
 * Everything on this panel is PropelAuth's: the invitation, the email, its
 * acceptance, the pending list and removal. Veyso stores no teams of its own,
 * so there is no Veyso endpoint behind any of this — a second copy of
 * membership would be a second thing to keep in step, and the one Veyso keeps
 * is a cached `orgId` on the account.
 *
 * What Veyso does own is what membership *means*: a project belongs to the
 * team, so everybody here opens the same work, and credit is the team's, so
 * everybody here spends the same balance. That is said on the panel rather
 * than left to be discovered, because inviting somebody to a shared wallet is
 * not a thing to find out afterwards.
 */
interface Member {
  user_id: string;
  email: string;
  role: string;
  can_be_deleted: boolean;
}

interface Invite {
  email: string;
  role: string;
  expires_at_seconds: number;
}

export const TeamPanel: React.FC = () => {
  const { fetchOrgMembers, fetchPendingOrgInvites, removeUserFromOrg, revokeUserOrgInvitation } = useAuthFrontendApis();
  // Shared with the onboarding step, so the two cannot word PropelAuth's
  // refusals differently — and so the stale-token refresh happens in one place
  // rather than being remembered twice.
  const { team, settling, invite: send, busy, error: inviteError, sent } = useInvite();

  /**
   * The team, from the token.
   *
   * Veyso puts each account in exactly one, so the first is the answer — the
   * map is the shape PropelAuth gives and pretending otherwise would break the
   * day somebody is genuinely in two.
   */
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  /** Failures from removing or withdrawing, which the invite hook knows nothing about. */
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!team) return;

    const people = await fetchOrgMembers(team, { page_size: 50, page_number: 0 });
    people.handle({
      success: (data) => setMembers(data.users as Member[]),
      unexpectedOrUnhandled: () => setMembers([]),
    });

    const pending = await fetchPendingOrgInvites(team, { page_size: 50, page_number: 0 });
    pending.handle({
      success: (data) => setInvites(data.pending_invites),
      // Silent. A pending list that will not load is not worth an error beside
      // a members list that did.
      unexpectedOrUnhandled: () => {},
    });
  }, [team, fetchOrgMembers, fetchPendingOrgInvites]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await send(email)) {
      setEmail("");
      void load();
    }
  };

  const remove = async (member: Member) => {
    if (!team) return;
    const response = await removeUserFromOrg({ org_id: team, user_id: member.user_id });
    response.handle({
      success: () => void load(),
      unexpectedOrUnhandled: () => setError(`Could not remove ${member.email}.`),
    });
  };

  const revoke = async (pending: Invite) => {
    if (!team) return;
    const response = await revokeUserOrgInvitation({ org_id: team, email: pending.email });
    response.handle({
      success: () => void load(),
      unexpectedOrUnhandled: () => setError(`Could not withdraw the invitation to ${pending.email}.`),
    });
  };

  if (!team) {
    return (
      <section className="panel p-5">
        <h2 className="text-[13px] font-semibold text-ink-100">{"Team"}</h2>
        {/* Two quite different situations, and the second used to be worded as
            the first. An account signing in with a token issued before its
            team existed is waiting, not broken — and telling somebody to sign
            out and back in is asking them to do by hand what the refresh above
            does on its own. */}
        <p className="mt-2 text-[12.5px] leading-relaxed text-ink-300">
          {settling
            ? "Setting your team up…"
            : "We could not set a team up for this workspace. Reload the page, and if it says this again, get in touch."}
        </p>
      </section>
    );
  }

  return (
    <section className="panel p-5">
      <h2 className="text-[13px] font-semibold text-ink-100">{"Team"}</h2>
      {/* Said before the invitation, not after. Somebody you invite can open
          your projects and spend your credit, and that is the whole point —
          but it is not a thing to discover once they have. */}
      <p className="mt-2 text-[12.5px] leading-relaxed text-ink-300">
        {
          "Everyone here sees the same projects and spends from the same balance. Your free videos are the team's, not one each."
        }
      </p>

      <form onSubmit={(event) => void invite(event)} className="mt-4 flex items-start gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="colleague@company.com"
          disabled={busy}
          className="h-[34px] min-w-0 flex-1 rounded-[8px] border border-[var(--edge)] bg-bay-850 px-3 text-[13px] text-ink-100 placeholder:text-ink-400 focus:border-ember-500 focus:outline-none"
        />
        <button type="submit" className="btn btn-primary shrink-0" disabled={busy || email.trim() === ""}>
          {busy ? "Sending…" : "Invite"}
        </button>
      </form>

      {inviteError || error ? <ErrorNote>{inviteError ?? error}</ErrorNote> : null}
      {sent ? <p className="mt-2 text-[12px] text-ink-300">{`Invitation sent to ${sent}.`}</p> : null}

      {members === null ? (
        <div className="mt-4">
          <Spinner />
        </div>
      ) : (
        <ul className="mt-4 flex flex-col">
          {members.map((member) => (
            <Row
              key={member.user_id}
              label={member.email}
              detail={member.role}
              action={member.can_be_deleted ? { label: "Remove", onClick: () => void remove(member) } : null}
            />
          ))}
          {invites.map((pending) => (
            <Row
              key={`invite-${pending.email}`}
              label={pending.email}
              detail="Invited — waiting for them to accept"
              action={{ label: "Withdraw", onClick: () => void revoke(pending) }}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

const Row: React.FC<{
  label: string;
  detail: string;
  action: { label: string; onClick: () => void } | null;
}> = ({ label, detail, action }) => (
  <li className="flex items-center justify-between gap-3 border-b border-[var(--edge)] py-2.5 text-[12.5px] last:border-b-0">
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-ink-100">{label}</span>
      <span className="text-[11px] text-ink-400">{detail}</span>
    </span>
    {action ? (
      <button className="btn btn-ghost btn-sm shrink-0" onClick={action.onClick}>
        {action.label}
      </button>
    ) : null}
  </li>
);
